/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Small utility helpers implemented with vanilla TypeScript.
 * Goal: eliminate the external utility dependency while preserving behavior where it matters.
 */

export type DebouncedFunc<T extends (...args: any[]) => any> = ((
  ...args: Parameters<T>
) => void) & {
  cancel: () => void
  flush: () => ReturnType<T> | undefined
}

type NormalizeDefault<T> = T extends readonly never[]
  ? any[]
  : T extends string
    ? string
    : T extends number
      ? number
      : T extends boolean
        ? boolean
        : T extends Record<string, never>
          ? any
          : T

const isObjectLike = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

type Path = string | ReadonlyArray<string | number>

const toPathTokens = (path: Path): string[] => {
  if (Array.isArray(path)) return path.map(String)
  // supports: a.b.c, a[0].b, a['b'].c (quotes are treated as part of token)
  // This matches common usage in this repo; not a full path parser.
  if (typeof path !== 'string') return []
  return path.match(/[^.[\]]+/g) ?? []
}

export const noop = (): void => undefined

export const isUndefined = (value: unknown): value is undefined =>
  value === undefined

export const isNull = (value: unknown): value is null => value === null

export const isNil = (value: unknown): value is null | undefined =>
  value === null || value === undefined

export const isBoolean = (value: unknown): value is boolean =>
  typeof value === 'boolean'

export const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && !Number.isNaN(value)

/**
 * Rough compatibility `isEmpty`:
 * - null/undefined -> true
 * - string/array -> length === 0
 * - map/set -> size === 0
 * - plain object -> Object.keys().length === 0
 * - everything else (number, boolean, function, date) -> true
 */
export const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.length === 0
  if (Array.isArray(value)) return value.length === 0
  if (value instanceof Map || value instanceof Set) return value.size === 0
  if (typeof value === 'object')
    return Object.keys(value as object).length === 0
  return true
}

export function get(obj: any, path: Path): any
export function get<TDefault>(
  obj: any,
  path: Path,
  defaultValue: TDefault
): NormalizeDefault<TDefault>
export function get(path: Path, obj: any): any
export function get<TDefault>(
  path: Path,
  obj: any,
  defaultValue: TDefault
): NormalizeDefault<TDefault>
export function get(path: Path): (obj: any) => any
export function get<TDefault>(
  path: Path,
  defaultValue: TDefault
): (obj: any) => NormalizeDefault<TDefault>
export function get<TDefault = any>(
  arg1: unknown,
  arg2?: unknown,
  arg3?: unknown
): any {
  // lodash-style: get(obj, path, default?) — detect by a path-like second argument.
  // This must run before the fp-style branch so arrays can be used as `obj`.
  if (
    arguments.length >= 2 &&
    (typeof arg2 === 'string' || Array.isArray(arg2))
  ) {
    const obj = arg1 as any
    const path = arg2 as Path
    const defaultValue = arg3 as TDefault
    if (obj === null || obj === undefined) return defaultValue
    if (!path || (typeof path === 'string' && path.length === 0)) return obj

    const tokens = toPathTokens(path)
    let cur: any = obj
    for (const token of tokens) {
      if (cur === null || cur === undefined) return defaultValue
      cur = cur[token]
    }
    return cur === undefined ? defaultValue : cur
  }

  // fp-style: get(path, obj, default?) or get(path, default?)(obj)
  if (typeof arg1 === 'string' || Array.isArray(arg1)) {
    const path = arg1 as Path

    // get(path, obj, default?)
    if (
      arguments.length >= 2 &&
      (typeof arg2 === 'object' || typeof arg2 === 'function')
    ) {
      const obj = arg2 as any
      const defaultValue = arg3 as TDefault
      return get(obj, path, defaultValue)
    }

    // get(path, default?)(obj)
    if (arguments.length >= 2) {
      const defaultValue = arg2 as TDefault
      return (obj: any) => get(obj, path, defaultValue)
    }
    return (obj: any) => get(obj, path)
  }

  // Fallback for unexpected call shapes.
  return arg3 as TDefault
}

export function set(obj: any, path: string, value: unknown): any {
  const tokens = toPathTokens(path)
  if (tokens.length === 0) return obj

  let cur: any = obj
  for (let i = 0; i < tokens.length - 1; i += 1) {
    const key = tokens[i]!
    const nextKey = tokens[i + 1]!
    const shouldBeArray = /^[0-9]+$/.test(nextKey)

    if (!isObjectLike(cur[key])) {
      cur[key] = shouldBeArray ? [] : {}
    }
    cur = cur[key]
  }
  cur[tokens[tokens.length - 1]!] = value
  return obj
}

export const includes = (
  collection: unknown,
  value: unknown,
  fromIndex = 0
): boolean => {
  if (typeof collection === 'string') {
    return collection.includes(String(value), fromIndex)
  }
  if (Array.isArray(collection)) {
    return collection.includes(value as never, fromIndex)
  }
  return false
}

export const values = (obj: any): any[] => Object.values(obj ?? {}) as any[]

export const uniq = <T>(arr: readonly T[] | null | undefined): T[] =>
  Array.from(new Set(arr ?? []))

export const divide = (dividend: number, divisor: number): number =>
  dividend / divisor

export const toLower = (str: unknown): string => String(str ?? '').toLowerCase()

export const toUpper = (str: unknown): string => String(str ?? '').toUpperCase()

const words = (input: unknown): string[] =>
  String(input ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)

export const lowerCase = (input: unknown): string =>
  words(input).join(' ').toLowerCase()

export const upperCase = (input: unknown): string =>
  words(input).join(' ').toUpperCase()

export const startCase = (input: unknown): string =>
  words(input)
    .map(w => (w ? w[0]!.toUpperCase() + w.slice(1).toLowerCase() : w))
    .join(' ')

export const snakeCase = (input: unknown): string =>
  words(input)
    .map(w => w.toLowerCase())
    .join('_')

export const capitalize = (input: unknown): string => {
  const s = String(input ?? '')
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1).toLowerCase()
}

export const mapValues = <T extends Record<string, any>, R>(
  obj: T,
  iteratee: (value: any, key: string, object: T) => R
): Record<string, R> => {
  const out: Record<string, R> = {}
  Object.keys(obj).forEach(key => {
    out[key] = iteratee(obj[key], key, obj)
  })
  return out
}

export const omit = (obj: any, keys: readonly string[] | string): any => {
  const toOmit = Array.isArray(keys) ? keys : [keys]
  const set = new Set<string>(toOmit)
  const out: Record<string, unknown> = {}
  Object.keys(obj ?? {}).forEach(k => {
    if (!set.has(k)) out[k] = obj[k]
  })
  return out
}

type PredicateShorthand =
  | ((...args: any[]) => boolean)
  | readonly [string, unknown]
  | Record<string, unknown>

const matchesPredicate = <T>(
  item: T,
  predicate: PredicateShorthand
): boolean => {
  if (typeof predicate === 'function') return !!predicate(item)

  if (Array.isArray(predicate)) {
    const [path, value] = predicate
    return isEqual(get(item, path), value)
  }

  // matches all provided keys on the item (shallow)
  return Object.entries(predicate).every(([k, v]) =>
    isEqual((item as any)?.[k], v)
  )
}

export const filter = (
  collection: any,
  predicate:
    | ((value: any, index: number, collection: any[]) => boolean)
    | readonly [string, unknown]
    | Record<string, unknown>
): any[] => {
  const list = Array.isArray(collection) ? collection : []

  // lodash-compatible: when predicate is a function, forward (value, index, array)
  // so callers can write `filter(arr, (v, i, a) => ...)`.
  if (typeof predicate === 'function') {
    return list.filter((value, index, array) => predicate(value, index, array))
  }

  return list.filter(item =>
    matchesPredicate(item, predicate as PredicateShorthand)
  )
}

export const groupBy = (
  collection: any,
  iteratee: any
): Record<string, any[]> => {
  const list = Array.isArray(collection) ? collection : []
  const fn =
    typeof iteratee === 'function'
      ? iteratee
      : (item: any) => String(item?.[iteratee])

  return list.reduce<Record<string, any[]>>((acc, item) => {
    const key = fn(item)
    ;(acc[key] ??= []).push(item)
    return acc
  }, {})
}

type Order = 'asc' | 'desc'
type Iteratee<T> = ((value: T) => unknown) | keyof T | string

const getIterateeValue = <T>(item: T, it: Iteratee<T>): unknown => {
  if (typeof it === 'function') return it(item)
  if (typeof it === 'string') return get(item, it)
  return (item as any)[it]
}

export const orderBy = <T>(
  collection: readonly T[] | null | undefined,
  iteratees: readonly Iteratee<T>[] | Iteratee<T> = [],
  orders: readonly Order[] | Order = []
): T[] => {
  const list = (collection ?? []) as readonly T[]

  const its = Array.isArray(iteratees)
    ? iteratees.length
      ? iteratees
      : [(x: T) => x]
    : [iteratees]

  const ords = Array.isArray(orders)
    ? orders.length
      ? orders
      : its.map(() => 'asc' as const)
    : [orders]

  const mapped = list.map((value, index) => ({
    value,
    index,
    criteria: its.map(it => getIterateeValue(value, it)),
  }))

  mapped.sort((a, b) => {
    for (let i = 0; i < a.criteria.length; i += 1) {
      const av = a.criteria[i]
      const bv = b.criteria[i]
      if (av === bv) continue

      const dir = ords[i] === 'desc' ? -1 : 1
      if (av === undefined) return 1 * dir
      if (bv === undefined) return -1 * dir
      if (av === null) return 1 * dir
      if (bv === null) return -1 * dir

      if (av < (bv as any)) return -1 * dir
      if (av > (bv as any)) return 1 * dir
    }
    return a.index - b.index
  })

  return mapped.map(x => x.value)
}

export const cloneDeep = <T>(value: T): T => {
  const sc = (globalThis as any).structuredClone as
    | undefined
    | ((v: unknown) => unknown)
  if (typeof sc === 'function') return sc(value) as T

  const inner = (v: any): any => {
    if (v === null || v === undefined) return v
    if (Array.isArray(v)) return v.map(inner)
    if (v instanceof Date) return new Date(v.getTime())
    if (v instanceof Map)
      return new Map(Array.from(v.entries(), ([k, val]) => [k, inner(val)]))
    if (v instanceof Set) return new Set(Array.from(v.values(), inner))
    if (typeof v === 'object') {
      const out: Record<string, unknown> = {}
      for (const [k, val] of Object.entries(v)) out[k] = inner(val)
      return out
    }
    return v
  }

  return inner(value) as T
}

export const isEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true
  if (typeof a !== typeof b) return false
  if (a === null || b === null || a === undefined || b === undefined)
    return false

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i += 1) {
      if (!isEqual(a[i], b[i])) return false
    }
    return true
  }

  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime()
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false
    for (const [k, v] of a.entries()) {
      if (!b.has(k)) return false
      if (!isEqual(v, b.get(k))) return false
    }
    return true
  }
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false
    for (const v of a.values()) if (!b.has(v)) return false
    return true
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const ao = a as Record<string, unknown>
    const bo = b as Record<string, unknown>
    const aKeys = Object.keys(ao)
    const bKeys = Object.keys(bo)
    if (aKeys.length !== bKeys.length) return false
    for (const k of aKeys) {
      if (!Object.prototype.hasOwnProperty.call(bo, k)) return false
      if (!isEqual(ao[k], bo[k])) return false
    }
    return true
  }

  return false
}

/**
 * Compatibility helpers (curried forms used in this repo)
 */
export function equals(
  a: unknown,
  b?: unknown
): boolean | ((bb: unknown) => boolean) {
  if (arguments.length === 1) return (bb: unknown) => isEqual(a, bb)
  return isEqual(a, b)
}

export const pipe =
  (...fns: Array<(x: any) => any>) =>
  (input: any): any =>
    fns.reduce((acc, fn) => fn(acc), input)

export const compose =
  (...fns: Array<(x: any) => any>) =>
  (input: any): any =>
    fns.reduceRight((acc, fn) => fn(acc), input)

type DebounceOptions = {
  leading?: boolean
  trailing?: boolean
  maxWait?: number
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait?: number,
  options?: DebounceOptions
): DebouncedFunc<T>
export function debounce<T extends (...args: any[]) => any>(
  wait: number
): (func: T) => DebouncedFunc<T>
export function debounce<T extends (...args: any[]) => any>(
  wait: number,
  func: T
): DebouncedFunc<T>
export function debounce<T extends (...args: any[]) => any>(
  arg1: unknown,
  arg2?: unknown
): unknown {
  // fp-style: debounce(wait)(fn) or debounce(wait, fn)
  if (typeof arg1 === 'number') {
    const wait = arg1
    if (typeof arg2 === 'function') return debounce(arg2 as T, wait)
    return (func: T) => debounce(func, wait)
  }

  const func = arg1 as T
  const wait = typeof arg2 === 'number' ? arg2 : 0
  const options =
    (arguments.length >= 3 ? (arguments[2] as DebounceOptions) : undefined) ??
    {}
  const leading = !!options.leading
  const trailing = options.trailing !== false
  const maxWait =
    typeof options.maxWait === 'number' ? options.maxWait : undefined

  let timeoutId: ReturnType<typeof setTimeout> | undefined
  let maxTimeoutId: ReturnType<typeof setTimeout> | undefined
  let lastArgs: Parameters<T> | undefined
  let lastInvokeTime = 0

  const invoke = () => {
    lastInvokeTime = Date.now()
    const args = lastArgs
    lastArgs = undefined
    if (args) func(...args)
  }

  const debounced = ((...args: Parameters<T>) => {
    lastArgs = args
    const now = Date.now()

    const shouldInvokeLeading = leading && !timeoutId
    if (shouldInvokeLeading) {
      invoke()
    }

    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      timeoutId = undefined
      if (trailing && lastArgs) invoke()
    }, wait)

    if (maxWait !== undefined && !maxTimeoutId) {
      const timeSinceLastInvoke = now - lastInvokeTime
      const remaining = Math.max(0, maxWait - timeSinceLastInvoke)
      maxTimeoutId = setTimeout(() => {
        maxTimeoutId = undefined
        if (timeoutId) clearTimeout(timeoutId)
        timeoutId = undefined
        if (lastArgs) invoke()
      }, remaining)
    }
  }) as DebouncedFunc<T>

  debounced.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = undefined
    if (maxTimeoutId) clearTimeout(maxTimeoutId)
    maxTimeoutId = undefined
    lastArgs = undefined
  }

  debounced.flush = () => {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = undefined
    if (maxTimeoutId) clearTimeout(maxTimeoutId)
    maxTimeoutId = undefined
    if (!lastArgs) return undefined
    const args = lastArgs
    lastArgs = undefined
    lastInvokeTime = Date.now()
    return func(...args)
  }

  return debounced
}

let uniqueIdCounter = 0
export const uniqueId = (prefix = ''): string => {
  uniqueIdCounter += 1
  return `${prefix}${uniqueIdCounter}`
}
